import React, { useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const ROLES = ["admin", "readonly"];
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/* 🎨 Role colors */
const roleStyles = {
  admin: {
    bg: "#ede9fe",
    color: "#5b21b6",
  },
  readonly: {
    bg: "#e0f2fe",
    color: "#0369a1",
  },
  unknown: {
    bg: "#f1f5f9",
    color: "#475569",
  },
};

/* 🎨 Status */
const statusStyles = {
  CONFIRMED: { label: "Confirmed", bg: "#dcfce7", color: "#166534" },
  UNCONFIRMED: { label: "Pending", bg: "#fef9c3", color: "#854d0e" },
};

const getInitials = (username = "") =>
  username.slice(0, 2).toUpperCase();

/* 🎨 Avatar gradient */
const getAvatarColor = (name = "") => {
  const colors = [
    ["#6366f1", "#8b5cf6"],
    ["#0ea5e9", "#06b6d4"],
    ["#10b981", "#22c55e"],
    ["#f59e0b", "#f97316"],
  ];
  const index = name.length % colors.length;
  return colors[index];
};

const UsersTable = ({
  users,
  loadingMap = {},
  onToggleEnable,
  onRoleChange,
  onDeleteClick,
  isReadonly = false, // ✅ NEW PROP
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginated = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <TableContainer
        sx={{
          borderRadius: 4,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <Table>
          {/* HEADER */}
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Active</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          {/* BODY */}
          <TableBody>
            {paginated.map((user) => {
              const isLoading = !!loadingMap[user.username];
              const isValidRole = ROLES.includes(user.role);

              const roleStyle =
                roleStyles[user.role] || roleStyles.unknown;

              const status =
                statusStyles[user.status] || {
                  label: user.status || "Unknown",
                  bg: "#e5e7eb",
                  color: "#374151",
                };

              const [c1, c2] = getAvatarColor(user.username);

              return (
                <TableRow
                  key={user.username}
                  sx={{
                    transition: "all 0.2s ease",
                    "&:hover": {
                      background: "#f9fafb",
                    },
                  }}
                >
                  {/* USER */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        sx={{
                          background: `linear-gradient(135deg, ${c1}, ${c2})`,
                          width: 40,
                          height: 40,
                          fontWeight: 700,
                        }}
                      >
                        {getInitials(user.username)}
                      </Avatar>

                      <Typography fontWeight={600}>
                        {user.username}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* EMAIL */}
                  <TableCell>
                    <Typography color="text.secondary">
                      {user.email}
                    </Typography>
                  </TableCell>

                  {/* ROLE */}
                  <TableCell>
                    <Tooltip
                      title={
                        isReadonly
                          ? "You don’t have permission to change roles"
                          : ""
                      }
                    >
                      <span>
                        <FormControl size="small">
                          <Select
                            value={isValidRole ? user.role : ""}
                            onChange={(e) =>
                              onRoleChange(user, e.target.value)
                            }
                            displayEmpty
                            disabled={isReadonly || isLoading} // ✅ KEY
                            sx={{
                              borderRadius: 3,
                              fontWeight: 600,
                              background: roleStyle.bg,
                              color: roleStyle.color,
                              minWidth: 120,
                            }}
                          >
                            {!isValidRole && (
                              <MenuItem value="">
                                <em>Unknown</em>
                              </MenuItem>
                            )}

                            {ROLES.map((r) => (
                              <MenuItem key={r} value={r}>
                                {r}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* STATUS */}
                  <TableCell>
                    <Chip
                      label={status.label}
                      size="small"
                      sx={{
                        background: status.bg,
                        color: status.color,
                        fontWeight: 600,
                        borderRadius: "999px",
                      }}
                    />
                  </TableCell>

                  {/* ACTIVE */}
                  <TableCell>
                    <Tooltip
                      title={
                        isReadonly
                          ? "You don’t have permission to change status"
                          : ""
                      }
                    >
                      <span>
                        {isLoading ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Switch
                            checked={!!user.enabled}
                            onChange={() => onToggleEnable(user)}
                            disabled={isReadonly} // ✅ KEY
                          />
                        )}
                      </span>
                    </Tooltip>
                  </TableCell>

                  {/* DELETE */}
                  <TableCell>
                    <Tooltip
                      title={
                        isReadonly
                          ? "You don’t have permission to delete"
                          : "Delete user"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={() => onDeleteClick(user)}
                          disabled={isReadonly || isLoading} // ✅ KEY
                          sx={{
                            color: "#ef4444",
                            "&:hover": {
                              background: "#fee2e2",
                            },
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}

            {/* EMPTY */}
            {paginated.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography sx={{ py: 4 }} color="text.secondary">
                    No users found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* PAGINATION */}
      <TablePagination
        component="div"
        count={users.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        sx={{
          borderTop: "1px solid #e5e7eb",
        }}
      />
    </Box>
  );
};

export default UsersTable;